import * as XLSX from "xlsx";
import type { Content, TableCell } from "pdfmake/interfaces";
import { formatDateEndOfDay } from "helpers/formatDate";
import { formatCurrency } from "helpers/utils";
import type { IAdiantamentoEvento } from "interfaces/adiantamento";
import type { ContaPagarView } from "layouts/pages/loja/financeiro/dashboard/types/contas-pagar.types";
import { getWithColsExportExcel } from "helpers/exportFileToExcel";

export function totalDespesasEvento(despesas: ContaPagarView[]): number {
  return despesas
    .filter((d) => d.statusTipo !== "cancelado")
    .reduce((acc, d) => acc + d.valorTotal, 0);
}

export function totalAdiantamentosEvento(
  adiantamentos: IAdiantamentoEvento[],
  apenasPago = false,
): number {
  return adiantamentos
    .filter((item) => !apenasPago || item.status === "Pago")
    .reduce((acc, item) => acc + Number(item.valorTransacao || 0), 0);
}

export function totalDespesasPagasEvento(despesas: ContaPagarView[]): number {
  return despesas
    .filter((d) => d.statusTipo === "pago")
    .reduce((acc, d) => acc + d.valorTotal, 0);
}

export function totalDespesasEmAbertoEvento(despesas: ContaPagarView[]): number {
  return despesas
    .filter((d) => d.statusTipo !== "cancelado" && d.statusTipo !== "pago")
    .reduce((acc, d) => acc + d.valorAberto, 0);
}

export type ResumoFinanceiroEventoInput = {
  qtdInscricoesPagas: number;
  totalArrecadadoInscricoes: number;
  taxasAdministrativas: number;
  descontosConcedidos: number;
  recebimentosTodos: number;
  recebimentosPagos: number;
  valorServico: number;
  taxasBancarias: number;
  totalDespesas: number;
  despesasPagas: number;
  despesasEmAberto: number;
};

export type TotalsResumoInput = {
  totalArrecadado: { qtd: number; valor: number };
  totalTaxaAdministrativa: { valor: number };
  totalDesconto: { valor: number };
};

export type IndicadoresEventoPdf = {
  totalInscricoes: number;
  inscricoesPagas: number;
  inscricoesCanceladas: number;
  totalAtletas: number;
  atletasCortesia: number;
  taxaInscricaoUnitaria: number;
};

export type TaxasBancariasPdfInput = {
  pix: { taxa: string; total: number };
  cartao: { taxa: string; total: number };
  boleto: { taxa: string; total: number };
};

export function montarResumoFinanceiroEvento(input: {
  totals: TotalsResumoInput;
  adiantamentos: IAdiantamentoEvento[];
  despesas: ContaPagarView[];
  valorServico: number;
  taxasBancarias: number;
}): ResumoFinanceiroEventoInput {
  const { totals, adiantamentos, despesas, valorServico, taxasBancarias } =
    input;
  return {
    qtdInscricoesPagas: totals.totalArrecadado.qtd,
    totalArrecadadoInscricoes: totals.totalArrecadado.valor,
    taxasAdministrativas: totals.totalTaxaAdministrativa.valor,
    descontosConcedidos: totals.totalDesconto.valor,
    recebimentosTodos: totalAdiantamentosEvento(adiantamentos),
    recebimentosPagos: totalAdiantamentosEvento(adiantamentos, true),
    valorServico,
    taxasBancarias,
    totalDespesas: totalDespesasEvento(despesas),
    despesasPagas: totalDespesasPagasEvento(despesas),
    despesasEmAberto: totalDespesasEmAbertoEvento(despesas),
  };
}

export function calcularMetricasResumoFinanceiro(
  resumo: ResumoFinanceiroEventoInput,
) {
  const valorProvaSemTaxa =
    resumo.totalArrecadadoInscricoes - resumo.taxasAdministrativas;
  const totalEntradas =
    resumo.totalArrecadadoInscricoes +
    resumo.recebimentosPagos +
    resumo.valorServico;
  const totalSaidas = resumo.taxasBancarias + resumo.totalDespesas;
  const resultadoLiquido = totalEntradas - totalSaidas;
  const saldoOperacional =
    resumo.totalArrecadadoInscricoes +
    resumo.valorServico -
    resumo.taxasBancarias;

  return {
    valorProvaSemTaxa,
    totalEntradas,
    totalSaidas,
    resultadoLiquido,
    saldoOperacional,
  };
}

function recebimentosResumoIguais(resumo: ResumoFinanceiroEventoInput): boolean {
  return resumo.recebimentosTodos === resumo.recebimentosPagos;
}

function linhasRecebimentosResumoExcel(
  resumo: ResumoFinanceiroEventoInput,
): string[][] {
  if (recebimentosResumoIguais(resumo)) {
    return [
      [
        "Recebimentos de evento (todos confirmados — status Pago)",
        formatCurrency(resumo.recebimentosPagos),
      ],
    ];
  }
  const pendentes = resumo.recebimentosTodos - resumo.recebimentosPagos;
  return [
    [
      "Recebimentos — todos os registros",
      formatCurrency(resumo.recebimentosTodos),
    ],
    [
      "Recebimentos pendentes (Solicitado / Agendado)",
      formatCurrency(pendentes),
    ],
    [
      "Recebimentos confirmados (status Pago)",
      formatCurrency(resumo.recebimentosPagos),
    ],
  ];
}

function pushLinhasRecebimentosResumoPdf(
  body: TableCell[][],
  resumo: ResumoFinanceiroEventoInput,
): void {
  if (recebimentosResumoIguais(resumo)) {
    body.push(
      pdfResumoDataRow(
        "Recebimentos de evento (todos confirmados — status Pago)",
        formatCurrency(resumo.recebimentosPagos),
      ),
    );
    return;
  }
  const pendentes = resumo.recebimentosTodos - resumo.recebimentosPagos;
  body.push(
    pdfResumoDataRow(
      "Recebimentos — todos os registros",
      formatCurrency(resumo.recebimentosTodos),
    ),
    pdfResumoDataRow(
      "Recebimentos pendentes (Solicitado / Agendado)",
      formatCurrency(pendentes),
      { indent: 12, valueColor: "#666666" },
    ),
    pdfResumoDataRow(
      "Recebimentos confirmados (status Pago)",
      formatCurrency(resumo.recebimentosPagos),
      { bold: true },
    ),
  );
}

const PDF_HEADER_FILL = "#CCCCCC";
const PDF_SECTION_FILL = "#E8E8E8";
const PDF_RESULT_FILL = "#F5F5F5";

function pdfResumoHeaderRow(label: string, valueLabel = "Valor"): TableCell[] {
  return [
    {
      text: label,
      bold: true,
      fillColor: PDF_HEADER_FILL,
      margin: [4, 4, 4, 4],
      fontSize: 10,
    },
    {
      text: valueLabel,
      alignment: "right",
      bold: true,
      fillColor: PDF_HEADER_FILL,
      margin: [4, 4, 4, 4],
      fontSize: 10,
    },
  ];
}

function pdfResumoSectionRow(title: string): TableCell[] {
  return [
    {
      text: title,
      colSpan: 2,
      bold: true,
      fillColor: PDF_SECTION_FILL,
      margin: [4, 6, 4, 4],
      fontSize: 10,
    },
    {},
  ];
}

function pdfResumoDataRow(
  label: string,
  value: string,
  opts?: {
    bold?: boolean;
    indent?: number;
    valueColor?: string;
    fontSize?: number;
  },
): TableCell[] {
  return [
    {
      text: label,
      alignment: "left",
      margin: [opts?.indent ?? 4, 2, 0, 2],
      fontSize: opts?.fontSize ?? 10,
      bold: opts?.bold,
    },
    {
      text: value,
      alignment: "right",
      margin: [0, 2, 4, 2],
      fontSize: opts?.fontSize ?? 10,
      bold: opts?.bold,
      color: opts?.valueColor,
    },
  ];
}

/** Demonstrativo financeiro consolidado (mesma lógica do Excel). */
export function buildResumoFinanceiroPdfContent(
  resumo: ResumoFinanceiroEventoInput,
  indicadores?: IndicadoresEventoPdf,
): Content[] {
  const metricas = calcularMetricasResumoFinanceiro(resumo);

  const body: TableCell[][] = [];

  if (indicadores) {
    body.push(
      pdfResumoSectionRow("INDICADORES DO EVENTO"),
      pdfResumoDataRow(
        "Total de inscrições",
        String(indicadores.totalInscricoes),
      ),
      pdfResumoDataRow(
        "Inscrições pagas",
        String(indicadores.inscricoesPagas),
      ),
      pdfResumoDataRow(
        "Inscrições canceladas",
        String(indicadores.inscricoesCanceladas),
      ),
      pdfResumoDataRow(
        "Atletas inscritos (incl. cortesia)",
        `${indicadores.totalAtletas} (cortesia: ${indicadores.atletasCortesia})`,
      ),
      pdfResumoDataRow(
        "Taxa administrativa unitária",
        formatCurrency(indicadores.taxaInscricaoUnitaria),
      ),
      [{ text: "", colSpan: 2, margin: [0, 4, 0, 0] }, {}],
    );
  }

  body.push(
    pdfResumoSectionRow("ENTRADAS"),
    pdfResumoDataRow(
      `Inscrições — valor da prova (sem taxa) · ${resumo.qtdInscricoesPagas} paga(s)`,
      formatCurrency(metricas.valorProvaSemTaxa),
    ),
    pdfResumoDataRow(
      "Taxas administrativas de inscrição",
      formatCurrency(resumo.taxasAdministrativas),
    ),
    pdfResumoDataRow(
      "Total arrecadado em inscrições (com taxa)",
      formatCurrency(resumo.totalArrecadadoInscricoes),
      { bold: true },
    ),
    pdfResumoDataRow(
      "Descontos concedidos (já abatidos no total)",
      formatCurrency(resumo.descontosConcedidos),
      { indent: 12, valueColor: "#666666" },
    ),
  );
  pushLinhasRecebimentosResumoPdf(body, resumo);

  if (resumo.valorServico > 0) {
    body.push(
      pdfResumoDataRow(
        "Valor do serviço",
        formatCurrency(resumo.valorServico),
      ),
    );
  }

  body.push(
    pdfResumoDataRow(
      "TOTAL DE ENTRADAS",
      formatCurrency(metricas.totalEntradas),
      { bold: true, fontSize: 11 },
    ),
    [{ text: "", colSpan: 2, margin: [0, 4, 0, 0] }, {}],
    pdfResumoSectionRow("SAÍDAS E CUSTOS"),
    pdfResumoDataRow(
      "Taxas bancárias (Pix, Boleto e Cartão)",
      `(-) ${formatCurrency(resumo.taxasBancarias)}`,
      { valueColor: "red" },
    ),
    pdfResumoDataRow(
      "Despesas do evento (exc. canceladas)",
      `(-) ${formatCurrency(resumo.totalDespesas)}`,
      { valueColor: "red" },
    ),
    pdfResumoDataRow(
      "· Despesas já pagas",
      formatCurrency(resumo.despesasPagas),
      { indent: 12 },
    ),
    pdfResumoDataRow(
      "· Despesas em aberto / vencidas",
      formatCurrency(resumo.despesasEmAberto),
      { indent: 12 },
    ),
    pdfResumoDataRow(
      "TOTAL DE SAÍDAS",
      `(-) ${formatCurrency(metricas.totalSaidas)}`,
      { bold: true, fontSize: 11, valueColor: "red" },
    ),
    [{ text: "", colSpan: 2, margin: [0, 6, 0, 0] }, {}],
    [
      {
        text: "RESULTADO LÍQUIDO DO EVENTO",
        bold: true,
        fillColor: PDF_RESULT_FILL,
        margin: [4, 8, 4, 8],
        fontSize: 12,
      },
      {
        text: formatCurrency(metricas.resultadoLiquido),
        alignment: "right",
        bold: true,
        fillColor: PDF_RESULT_FILL,
        margin: [4, 8, 4, 8],
        fontSize: 13,
        color:
          metricas.resultadoLiquido >= 0 ? "#1B5E20" : "#B71C1C",
      },
    ],
  );

  return [
    {
      text: "\nDemonstrativo financeiro do evento\n",
      bold: true,
      fontSize: 14,
    },
    {
      fontSize: 11,
      table: {
        widths: ["*", 115],
        body,
      },
      layout: {
        hLineWidth: (i: number, node: { table: { body: unknown[] } }) =>
          i === 0 || i === 1 || i === node.table.body.length ? 0.5 : 0.25,
        vLineWidth: () => 0,
        hLineColor: () => "#DDDDDD",
        paddingLeft: () => 0,
        paddingRight: () => 0,
      },
    },
    {
      margin: [0, 8, 0, 0],
      columns: [
        {
          text: "Saldo operacional (inscrições + serviço − taxas bancárias)",
          fontSize: 9,
          color: "gray",
          italics: true,
          width: "*",
        },
        {
          text: formatCurrency(metricas.saldoOperacional),
          fontSize: 10,
          alignment: "right",
          width: 115,
        },
      ],
    },
    {
      text: "O resultado líquido considera inscrições pagas, recebimentos confirmados (Pago), valor do serviço, taxas bancárias e despesas registradas (exceto canceladas). Detalhes nas seções abaixo.",
      fontSize: 8,
      color: "gray",
      italics: true,
      margin: [0, 4, 0, 0],
    },
  ];
}

export function buildTaxasBancariasPdfContent(
  taxas: TaxasBancariasPdfInput,
  totalTaxas: number,
): Content[] {
  return [
    {
      text: "\nDetalhamento — taxas bancárias\n",
      bold: true,
      fontSize: 12,
    },
    {
      fontSize: 10,
      table: {
        widths: ["*", 70, 90],
        body: [
          [
            {
              text: "Tipo",
              bold: true,
              fillColor: PDF_HEADER_FILL,
              margin: [4, 4, 4, 4],
            },
            {
              text: "Taxa",
              alignment: "right",
              bold: true,
              fillColor: PDF_HEADER_FILL,
              margin: [4, 4, 4, 4],
            },
            {
              text: "Total",
              alignment: "right",
              bold: true,
              fillColor: PDF_HEADER_FILL,
              margin: [4, 4, 4, 4],
            },
          ],
          [
            { text: "Pix", margin: [4, 3, 0, 3] },
            {
              text: `${taxas.pix.taxa}%`,
              alignment: "right",
              margin: [0, 3, 4, 3],
            },
            {
              text: formatCurrency(taxas.pix.total),
              alignment: "right",
              margin: [0, 3, 4, 3],
            },
          ],
          [
            { text: "Boleto", margin: [4, 3, 0, 3] },
            {
              text: formatCurrency(Number(taxas.boleto.taxa)),
              alignment: "right",
              margin: [0, 3, 4, 3],
            },
            {
              text: formatCurrency(taxas.boleto.total),
              alignment: "right",
              margin: [0, 3, 4, 3],
            },
          ],
          [
            { text: "Cartão de Crédito", margin: [4, 3, 0, 3] },
            {
              text: `${taxas.cartao.taxa}%`,
              alignment: "right",
              margin: [0, 3, 4, 3],
            },
            {
              text: formatCurrency(taxas.cartao.total),
              alignment: "right",
              margin: [0, 3, 4, 3],
            },
          ],
          [
            {
              text: "Total",
              bold: true,
              fillColor: PDF_SECTION_FILL,
              margin: [4, 4, 0, 4],
            },
            { text: "", fillColor: PDF_SECTION_FILL },
            {
              text: formatCurrency(totalTaxas),
              alignment: "right",
              bold: true,
              fillColor: PDF_SECTION_FILL,
              margin: [0, 4, 4, 4],
            },
          ],
        ],
      },
      layout: {
        hLineWidth: (i: number, node: { table: { body: unknown[] } }) =>
          i === 0 || i === node.table.body.length ? 0.5 : 0.25,
        vLineWidth: () => 0,
        hLineColor: () => "#DDDDDD",
      },
    },
  ];
}

export function buildAdiantamentosPdfContent(
  adiantamentos: IAdiantamentoEvento[],
): Content[] {
  const linhas: TableCell[][] =
    adiantamentos.length > 0
      ? adiantamentos.map((item) => [
          { text: item.identificacaoTransacao || "", alignment: "left" },
          {
            text: item.dataPagamento
              ? formatDateEndOfDay(item.dataPagamento)
              : "",
            alignment: "center",
          },
          {
            text: formatCurrency(item.valorTransacao) || "R$ 0,00",
            alignment: "right",
          },
          { text: item.status || "", alignment: "center", fontSize: 9 },
          { text: item.nomeResponsavel || "", alignment: "left" },
        ])
      : [
          [
            {
              text: "Nenhum recebimento registrado",
              colSpan: 5,
              alignment: "center",
              italics: true,
            },
            {},
            {},
            {},
            {},
          ],
        ];

  return [
    {
      text: "\nRecebimentos de evento\n",
      bold: true,
      fontSize: 14,
    },
    {
      fontSize: 10,
      table: {
        widths: ["*", 72, 72, 58, "*"],
        body: [
          [
            {
              text: "Descrição",
              alignment: "left",
              bold: true,
              fillColor: PDF_HEADER_FILL,
              margin: [4, 4, 4, 4],
            },
            {
              text: "Dt. pagamento",
              alignment: "center",
              bold: true,
              fillColor: PDF_HEADER_FILL,
              margin: [4, 4, 4, 4],
            },
            {
              text: "Valor",
              alignment: "right",
              bold: true,
              fillColor: PDF_HEADER_FILL,
              margin: [4, 4, 4, 4],
            },
            {
              text: "Status",
              alignment: "center",
              bold: true,
              fillColor: PDF_HEADER_FILL,
              margin: [4, 4, 4, 4],
            },
            {
              text: "Responsável",
              alignment: "left",
              bold: true,
              fillColor: PDF_HEADER_FILL,
              margin: [4, 4, 4, 4],
            },
          ],
          ...linhas,
        ],
      },
      layout: {
        hLineWidth: (i: number, node: { table: { body: unknown[] } }) =>
          i === 0 || i === 1 || i === node.table.body.length ? 0.5 : 0.25,
        vLineWidth: () => 0,
        hLineColor: () => "#DDDDDD",
      },
    },
    ...((): Content[] => {
      const totalTodos = totalAdiantamentosEvento(adiantamentos);
      const totalPago = totalAdiantamentosEvento(adiantamentos, true);
      if (totalTodos === totalPago) {
        return [
          {
            columns: [
              {
                text: "Total de recebimentos (todos confirmados)",
                bold: true,
                fontSize: 11,
                margin: [0, 8, 0, 4],
              },
              {
                text: formatCurrency(totalPago),
                alignment: "right",
                bold: true,
                fontSize: 11,
                margin: [0, 8, 0, 4],
              },
            ],
          },
        ];
      }
      return [
        {
          columns: [
            {
              text: "Total (todos os registros)",
              bold: true,
              fontSize: 11,
              margin: [0, 8, 0, 4],
            },
            {
              text: formatCurrency(totalTodos),
              alignment: "right",
              bold: true,
              fontSize: 11,
              margin: [0, 8, 0, 4],
            },
          ],
        },
        {
          columns: [
            {
              text: "Total confirmado (status Pago)",
              fontSize: 10,
              color: "#444444",
            },
            {
              text: formatCurrency(totalPago),
              alignment: "right",
              fontSize: 10,
              color: "#444444",
            },
          ],
        },
      ];
    })(),
  ];
}

function buildResumoGeralRows(
  tituloEvento: string,
  resumo: ResumoFinanceiroEventoInput,
): string[][] {
  const metricas = calcularMetricasResumoFinanceiro(resumo);

  const linhas: string[][] = [
    [`Resumo financeiro — ${tituloEvento}`, ""],
    [],
    ["Descrição", "Valor"],
    [],
    ["ENTRADAS", ""],
    [
      `Inscrições pagas — valor da prova (sem taxa administrativa) · ${resumo.qtdInscricoesPagas} inscrição(ões)`,
      formatCurrency(metricas.valorProvaSemTaxa),
    ],
    [
      "Taxas administrativas de inscrição",
      formatCurrency(resumo.taxasAdministrativas),
    ],
    [
      "Total arrecadado em inscrições (com taxa)",
      formatCurrency(resumo.totalArrecadadoInscricoes),
    ],
    [
      "Descontos concedidos (já abatidos no total arrecadado)",
      formatCurrency(resumo.descontosConcedidos),
    ],
    [],
    ...linhasRecebimentosResumoExcel(resumo),
  ];

  if (resumo.valorServico > 0) {
    linhas.push(["Valor do serviço", formatCurrency(resumo.valorServico)]);
  }

  linhas.push(
    ["TOTAL DE ENTRADAS (para resultado)", formatCurrency(metricas.totalEntradas)],
    [],
    ["SAÍDAS E CUSTOS", ""],
    [
      "Taxas bancárias (Pix, Boleto e Cartão)",
      `(-) ${formatCurrency(resumo.taxasBancarias)}`,
    ],
    [
      "Despesas do evento — total registrado (exc. canceladas)",
      `(-) ${formatCurrency(resumo.totalDespesas)}`,
    ],
    ["  · Despesas já pagas", formatCurrency(resumo.despesasPagas)],
    [
      "  · Despesas em aberto / vencidas",
      formatCurrency(resumo.despesasEmAberto),
    ],
    ["TOTAL DE SAÍDAS", `(-) ${formatCurrency(metricas.totalSaidas)}`],
    [],
    ["RESULTADO DO EVENTO", ""],
    [
      "Resultado líquido (entradas − saídas)",
      formatCurrency(metricas.resultadoLiquido),
    ],
    [],
    ["REFERÊNCIA", ""],
    [
      "Saldo operacional (inscrições + serviço − taxas bancárias)",
      formatCurrency(metricas.saldoOperacional),
    ],
    [],
    [
      "Nota",
      "O resultado líquido considera inscrições pagas, recebimentos confirmados (Pago), valor do serviço, taxas bancárias e todas as despesas registradas (exceto canceladas).",
    ],
  );

  return linhas;
}

function criarPlanilhaResumoGeral(
  tituloEvento: string,
  resumo: ResumoFinanceiroEventoInput,
): XLSX.WorkSheet {
  const rows = buildResumoGeralRows(tituloEvento, resumo);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
  ws["!cols"] = [{ wch: 62 }, { wch: 22 }];
  return ws;
}

/** Blocos pdfmake para a seção de despesas do relatório financeiro. */
export function buildDespesasPdfContent(despesas: ContaPagarView[]): Content[] {
  const total = totalDespesasEvento(despesas);

  const linhas: TableCell[][] =
    despesas.length > 0
      ? despesas.map((d) => [
          { text: d.dataVencimentoLabel, alignment: "center" },
          { text: d.descricao, alignment: "left" },
          { text: formatCurrency(d.valorTotal), alignment: "right" },
          { text: d.statusLabel, alignment: "center" },
        ])
      : [
          [
            {
              text: "Nenhuma despesa registrada",
              colSpan: 4,
              alignment: "center",
              italics: true,
            },
            {},
            {},
            {},
          ],
        ];

  return [
    {
      text: "\nDespesas\n",
      bold: true,
      fontSize: 14,
    },
    {
      fontSize: 11,
      table: {
        widths: [80, "*", 90, 80],
        body: [
          [
            {
              text: "Vencimento",
              alignment: "center",
              bold: true,
              fillColor: "#CCCCCC",
            },
            {
              text: "Descrição",
              alignment: "left",
              bold: true,
              fillColor: "#CCCCCC",
            },
            {
              text: "Valor total",
              alignment: "right",
              bold: true,
              fillColor: "#CCCCCC",
            },
            {
              text: "Status",
              alignment: "center",
              bold: true,
              fillColor: "#CCCCCC",
            },
          ],
          ...linhas,
        ],
      },
    },
    {
      text: `Total de despesas: ${formatCurrency(total)}\n`,
      margin: [0, 10, 0, 10],
      bold: true,
      fontSize: 12,
    },
  ];
}

type InscritoExcelRow = {
  ninsc: string | number;
  nome: string;
  dataNascimento: string;
  sexo: string;
  equipe: string;
  cidade: string;
  categoria: string;
  desconto: number;
  taxa: number;
  valorInscricao: number;
  totalInscricao: number;
  dataInscricao: string;
};

type ExportFinanceiroEventoExcelParams = {
  tituloEvento: string;
  inscritos: InscritoExcelRow[];
  despesas: ContaPagarView[];
  adiantamentos: IAdiantamentoEvento[];
  resumoFinanceiro: ResumoFinanceiroEventoInput;
  totaisResumo?: { description: string; value: string }[];
  filename: string;
};

export function exportFinanceiroEventoExcel({
  tituloEvento,
  inscritos,
  despesas,
  adiantamentos,
  resumoFinanceiro,
  totaisResumo,
  filename,
}: ExportFinanceiroEventoExcelParams): void {
  const wb = XLSX.utils.book_new();

  const wsResumo = criarPlanilhaResumoGeral(tituloEvento, resumoFinanceiro);
  XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

  const headerInscritos = [
    "N.INSC.",
    "NOME",
    "NASC.",
    "SEXO",
    "EQUIPE",
    "CIDADE/UF",
    "CAT",
    "DESC",
    "TAXA",
    "V.INSC",
    "TOTAL INSC.",
    "DATA INSC.",
  ];

  const wsInsc = XLSX.utils.json_to_sheet([]);
  XLSX.utils.sheet_add_aoa(
    wsInsc,
    [[`Relatório inscrições Atleta: ${tituloEvento}`, ""]],
    { origin: "A1" },
  );
  wsInsc["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }];
  XLSX.utils.sheet_add_aoa(wsInsc, [headerInscritos], { origin: "A3" });
  XLSX.utils.sheet_add_json(wsInsc, inscritos, {
    origin: "A4",
    skipHeader: true,
  });
  wsInsc["!cols"] = getWithColsExportExcel(headerInscritos, inscritos);

  const totalValorInscricao = inscritos.reduce(
    (acc, row) => acc + Number(row.valorInscricao || 0),
    0,
  );

  XLSX.utils.sheet_add_aoa(wsInsc, [[], []], { origin: -1 });
  XLSX.utils.sheet_add_aoa(wsInsc, [["Total", ""]], { origin: -1 });
  XLSX.utils.sheet_add_aoa(
    wsInsc,
    [["Total inscrição sem a taxa", formatCurrency(totalValorInscricao)]],
    { origin: -1 },
  );

  if (totaisResumo?.length) {
    totaisResumo.forEach((row) => {
      XLSX.utils.sheet_add_aoa(
        wsInsc,
        [[row.description, row.value]],
        { origin: -1 },
      );
    });
  }

  XLSX.utils.book_append_sheet(wb, wsInsc, "Inscritos");

  const headerDespesas = ["Vencimento", "Descrição", "Valor total", "Status"];
  const rowsDespesas = despesas.map((d) => ({
    vencimento: d.dataVencimentoLabel,
    descricao: d.descricao,
    valorTotal: formatCurrency(d.valorTotal),
    status: d.statusLabel,
  }));

  const wsDesp = XLSX.utils.json_to_sheet([]);
  XLSX.utils.sheet_add_aoa(
    wsDesp,
    [[`Despesas — ${tituloEvento}`, ""]],
    { origin: "A1" },
  );
  wsDesp["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
  XLSX.utils.sheet_add_aoa(wsDesp, [headerDespesas], { origin: "A3" });
  XLSX.utils.sheet_add_json(wsDesp, rowsDespesas, {
    origin: "A4",
    skipHeader: true,
  });
  wsDesp["!cols"] = getWithColsExportExcel(headerDespesas, rowsDespesas);
  XLSX.utils.sheet_add_aoa(wsDesp, [[], []], { origin: -1 });
  XLSX.utils.sheet_add_aoa(
    wsDesp,
    [["Total de despesas", formatCurrency(totalDespesasEvento(despesas))]],
    { origin: -1 },
  );

  XLSX.utils.book_append_sheet(wb, wsDesp, "Despesas");

  const headerRecebimentos = [
    "Solicitante",
    "ID transação",
    "Data solicitação",
    "Valor",
    "Data pagamento",
    "Status",
    "Responsável",
    "Observação",
  ];
  const rowsRecebimentos = adiantamentos.map((item) => ({
    solicitante: item.solicitante ?? "",
    identificacaoTransacao: item.identificacaoTransacao ?? "",
    dataSolicitacao: item.dataSolicitacao
      ? formatDateEndOfDay(item.dataSolicitacao)
      : "",
    valor: formatCurrency(item.valorTransacao),
    dataPagamento: item.dataPagamento
      ? formatDateEndOfDay(item.dataPagamento)
      : "",
    status: item.status ?? "",
    responsavel: item.nomeResponsavel ?? "",
    observacao: item.obs ?? "",
  }));

  const wsRec = XLSX.utils.json_to_sheet([]);
  XLSX.utils.sheet_add_aoa(
    wsRec,
    [[`Recebimentos de evento — ${tituloEvento}`, ""]],
    { origin: "A1" },
  );
  wsRec["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }];
  XLSX.utils.sheet_add_aoa(wsRec, [headerRecebimentos], { origin: "A3" });
  XLSX.utils.sheet_add_json(wsRec, rowsRecebimentos, {
    origin: "A4",
    skipHeader: true,
  });
  wsRec["!cols"] = getWithColsExportExcel(headerRecebimentos, rowsRecebimentos);
  XLSX.utils.sheet_add_aoa(wsRec, [[], []], { origin: -1 });
  XLSX.utils.sheet_add_aoa(
    wsRec,
    [
      [
        "Total de recebimentos",
        formatCurrency(totalAdiantamentosEvento(adiantamentos)),
      ],
    ],
    { origin: -1 },
  );

  XLSX.utils.book_append_sheet(wb, wsRec, "Recebimentos");

  XLSX.writeFile(wb, `${filename}.xlsx`, { compression: true });
}
